import React, { useState } from 'react';
import { Layers, Download, Info, TrendingUp } from 'lucide-react';
import Button from '../../../../../components/ui/Button';
import { formatCurrency, formatNumber } from '../../../../../utils/formatting';

/**
 * Компонент для отображения графика инвестиций в ИИ
 */
const InvestmentSchedule = ({ 
  schedule, 
  results, 
  showFullSchedule, 
  setShowFullSchedule 
}) => {
  // Состояние для выбора вкладки - всегда объявляем хук в начале компонента
  const [activeTab, setActiveTab] = useState('general');
  
  // Если нет данных для отображения
  if (!schedule || schedule.length === 0 || !results) {
    return null;
  }

  // Определение графика для отображения
  const displaySchedule = showFullSchedule 
    ? schedule 
    : schedule.slice(0, 12);

  // Расчет итоговых сумм
  const totals = {
    contribution: schedule.reduce((sum, item) => sum + item.contribution, 0),
    interest: schedule.reduce((sum, item) => sum + item.interest, 0),
    balance: schedule[schedule.length - 1].balance
  };

  // Функция форматирования даты
  const formatDate = (month) => {
    const date = new Date();
    date.setMonth(date.getMonth() + month - 1);
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 mt-8">
      {/* Заголовок */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-medium text-gray-900 dark:text-white flex items-center">
          <Layers className="w-5 h-5 mr-2 text-indigo-500" />
          График инвестиций в ИИ ({schedule.length} мес.)
        </h3>

        {/* Кнопка переключения полного/краткого графика */}
        {schedule.length > 12 && (
          <Button
            size="sm"
            onClick={() => setShowFullSchedule(prev => !prev)}
            variant="outline"
            aria-expanded={showFullSchedule}
          >
            {showFullSchedule 
              ? 'Показать краткий график' 
              : `Показать все ${schedule.length} мес.`}
          </Button>
        )}
      </div>

      {/* Вкладки */}
      <div className="flex space-x-1 border-b border-gray-200 dark:border-gray-700 mb-4">
        <button
          onClick={() => setActiveTab('general')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'general'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Общий график
        </button>
        <button
          onClick={() => setActiveTab('detailed')}
          className={`py-2 px-4 text-sm font-medium rounded-t-lg ${
            activeTab === 'detailed'
              ? 'text-indigo-600 dark:text-indigo-400 border-b-2 border-indigo-500 dark:border-indigo-400'
              : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
          }`}
        >
          Детализация
        </button>
      </div>

      {/* Информационная подсказка */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded p-3 mb-4 flex items-start">
        <Info className="text-blue-500 dark:text-blue-400 w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-700 dark:text-blue-300">
          <p>
            {showFullSchedule 
              ? "Полный график инвестиций в ИИ. Прокрутите таблицу вправо для просмотра всех данных." 
              : `Показаны первые ${displaySchedule.length} месяцев графика инвестиций.`}
          </p>
        </div>
      </div>

      {/* Контейнер таблицы с прокруткой */}
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[600px] payment-schedule-table">
        <table className="w-full text-sm text-left text-gray-700 dark:text-gray-300 min-w-[800px]">
          <thead className="text-xs text-gray-700 dark:text-gray-300 uppercase bg-gray-100 dark:bg-gray-700 sticky top-0 z-[1] shadow-sm">
            <tr>
              <th scope="col" className="px-3 py-2 font-semibold text-center">№</th>
              <th scope="col" className="px-3 py-2 font-semibold">Дата</th>

              {activeTab === 'general' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Взнос</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Баланс</th>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Взнос</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Совокупный взнос</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Совокупные проценты</th>
                  <th scope="col" className="px-3 py-2 font-semibold text-right">Баланс</th>
                </>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {displaySchedule.map((item) => (
              <tr
                key={item.month}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-100"
              >
                <td className="px-3 py-1.5 whitespace-nowrap text-center font-medium">{item.month}</td>
                <td className="px-3 py-1.5 whitespace-nowrap">{formatDate(item.month)}</td>

                {activeTab === 'general' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {formatCurrency(item.contribution, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                      {formatCurrency(item.interest, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.balance, 0)}
                    </td>
                  </>
                )}

                {activeTab === 'detailed' && (
                  <>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {formatCurrency(item.contribution, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                      {formatCurrency(item.interest, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right">
                      {formatCurrency(item.cumulativeContribution, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                      {formatCurrency(item.cumulativeInterest, 0)}
                    </td>
                    <td className="px-3 py-1.5 whitespace-nowrap text-right font-medium text-gray-800 dark:text-gray-100">
                      {formatCurrency(item.balance, 0)}
                    </td>
                  </>
                )}
              </tr>
            ))}

            {/* Итоговая строка */}
            <tr className="bg-gray-50 dark:bg-gray-700 font-semibold">
              <td className="px-3 py-2 whitespace-nowrap text-center" colSpan="2">Итого:</td>

              {activeTab === 'general' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatCurrency(totals.contribution, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                    {formatCurrency(totals.interest, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                </>
              )}

              {activeTab === 'detailed' && (
                <>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatCurrency(totals.contribution, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                    {formatCurrency(totals.interest, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">
                    {formatCurrency(results.totalContributions, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right text-green-600 dark:text-green-400">
                    {formatCurrency(results.totalInterest, 0)}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-right">-</td>
                </>
              )}
            </tr>
          </tbody>
        </table>
      </div>

      {/* Дополнительная информация */}
      <div className="mt-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-start gap-3">
          <TrendingUp className="w-5 h-5 text-green-500 mt-1" />
          <div>
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">
              Итоги инвестирования
            </h4>
            <div className="text-sm text-gray-600 dark:text-gray-400 space-y-1">
              <p>
                <span className="font-semibold">Общий взнос:</span>{' '}
                {formatCurrency(results.totalContributions, 0)}
              </p>
              <p>
                <span className="font-semibold">Доход от инвестиций:</span>{' '}
                {formatCurrency(results.totalInterest, 0)}
              </p>
              <p>
                <span className="font-semibold">Средняя годовая доходность:</span>{' '}
                {formatNumber(results.averageAnnualReturn, 2)}%
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Кнопка показа полного графика */}
      {!showFullSchedule && schedule.length > 12 && (
        <div className="text-center mt-4">
          <Button
            onClick={() => setShowFullSchedule(true)}
            icon={<Layers className="w-4 h-4" />}
            variant="link"
          >
            Показать все {schedule.length} месяцев
          </Button>
        </div>
      )}
    </div>
  );
};

export default InvestmentSchedule;